import React, { useState } from 'react';
import { Form, Input, Button, Typography, Card, message } from 'antd';
import { MailOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { scrollToFirstError } from '../utils/formHelpers';

const { Title, Text } = Typography;

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    try {
      const validatedValues = await form.validateFields();
      setLoading(true);
      // TODO: اینجا باید API call انجام شود
      // const response = await fetch('/api/auth/forgot-password', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email: validatedValues.email }),
      // });
      // const data = await response.json();
      
      // شبیه‌سازی ارسال ایمیل
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      message.success('ایمیل بازیابی رمز عبور با موفقیت ارسال شد');
      setEmailSent(true);
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else {
        message.error('خطا در ارسال ایمیل. لطفاً دوباره تلاش کنید');
      }
    } finally {
      setLoading(false);
    }
  };

  // مسیر تصویر بکگراند
  const backgroundImage = `${process.env.PUBLIC_URL || ''}/images/bg_image.webp`;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#f0f0f0',
      }}
    >
      {/* لایه تیره برای خوانایی بهتر */}
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60"></div>

      {/* محتوای اصلی */}
      <div className="relative z-10 w-full max-w-md">
        <Card
          className={`rounded-2xl shadow-2xl border-0 overflow-hidden ${
            isDarkMode 
              ? 'bg-gray-800/95 backdrop-blur-md' 
              : 'bg-white/95 backdrop-blur-md'
          }`}
          bodyStyle={{ padding: '3rem' }}
        >
          {/* عنوان */}
          <div className="text-center mb-8">
            <Title 
              level={2} 
              className={`!mb-2 !font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}
              style={{ fontFamily: 'CustomFont' }}
            >
              {emailSent ? 'ایمیل ارسال شد' : 'بازیابی رمز عبور'}
            </Title>
            {emailSent && (
              <Text 
                className={`text-base ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}
                style={{ fontFamily: 'CustomFont' }}
              >
                لطفاً صندوق ورودی ایمیل خود را بررسی کنید
              </Text>
            )}
          </div>

          {!emailSent ? (
            <>
              {/* فرم بازیابی رمز عبور */}
              <Form
                form={form}
                name="forgotPassword"
                onFinish={onFinish}
                layout="vertical"
                size="middle"
                className="mt-8"
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'لطفاً ایمیل خود را وارد کنید' },
                    { type: 'email', message: 'لطفاً یک ایمیل معتبر وارد کنید' },
                  ]}
                  className="mb-6"
                >
                  <Input
                    prefix={<MailOutlined className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />}
                    placeholder="ایمیل"
                    className={`h-12 rounded-lg border-2 transition-all ${
                      isDarkMode 
                        ? 'bg-gray-700/50 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-blue-500'
                    }`}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>

                <Form.Item className="mb-4">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    className="h-12 rounded-lg text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                    style={{ 
                      fontFamily: 'CustomFont',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                    }}
                    icon={<ArrowRightOutlined />}
                    iconPosition="end"
                  >
                    {loading ? 'در حال ارسال...' : 'ارسال لینک بازیابی'}
                  </Button>
                </Form.Item>
              </Form>

              {/* لینک بازگشت به لاگین */}
              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className={`
                    text-sm
                    ${isDarkMode 
                      ? 'text-blue-400 hover:text-blue-300' 
                      : 'text-blue-600 hover:text-blue-700'
                    }
                    transition-colors
                    inline-flex items-center gap-1
                  `}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  <ArrowRightOutlined className="transform rotate-180" />
                  بازگشت به صفحه ورود
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* پیام موفقیت */}
              <div className={`
                text-center p-6 rounded-lg mb-6
                ${isDarkMode 
                  ? 'bg-green-900/30 border border-green-700' 
                  : 'bg-green-50 border border-green-200'
                }
              `}>
                <MailOutlined className={`
                  text-4xl mb-4
                  ${isDarkMode ? 'text-green-400' : 'text-green-600'}
                `} />
                <Text 
                  className={`
                    block text-base
                    ${isDarkMode ? 'text-green-300' : 'text-green-700'}
                  `}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  لینک بازیابی رمز عبور به ایمیل شما ارسال شد.
                  <br />
                  لطفاً صندوق ورودی و پوشه اسپم خود را بررسی کنید.
                </Text>
              </div>

              {/* دکمه‌های عملیات */}
              <div className="space-y-3">
                <Button
                  type="primary"
                  block
                  onClick={() => {
                    setEmailSent(false);
                    form.resetFields();
                  }}
                  className="h-12 rounded-lg text-base font-semibold"
                  style={{ 
                    fontFamily: 'CustomFont',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                  }}
                >
                  ارسال مجدد ایمیل
                </Button>

                <Link to="/login" className="block">
                  <Button
                    block
                    className={`
                      h-12 rounded-lg text-base font-semibold
                      ${isDarkMode 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                      }
                    `}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    بازگشت به صفحه ورود
                  </Button>
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;

